/**
 * A zip of whole files, stored as they are (no compression): what a download of several exported
 * documents needs, without a library. A searchable PDF is compressed inside already, so storing
 * costs nothing worth a dependency. Names are UTF-8 (bit 11), sizes under 4 GB (no ZIP64).
 */
export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** A local file header (30 bytes) or a central directory record (46 bytes), little endian. */
function header(entry: ZipEntry, name: Uint8Array, crc: number, offset: number | null): Uint8Array {
  const central = offset !== null;
  const bytes = new Uint8Array((central ? 46 : 30) + name.length);
  const view = new DataView(bytes.buffer);
  let at = 0;
  const u16 = (value: number) => {
    view.setUint16(at, value, true);
    at += 2;
  };
  const u32 = (value: number) => {
    view.setUint32(at, value, true);
    at += 4;
  };
  u32(central ? 0x02014b50 : 0x04034b50);
  if (central) u16(20); // made by
  u16(20); // needed to extract
  u16(0x0800); // UTF-8 names
  u16(0); // stored
  u16(0); // time
  u16(0x21); // date: 1980-01-01
  u32(crc);
  u32(entry.data.length);
  u32(entry.data.length);
  u16(name.length);
  u16(0); // extra
  if (central) {
    u16(0); // comment
    u16(0); // disk
    u16(0); // internal attributes
    u32(0); // external attributes
    u32(offset);
  }
  bytes.set(name, at);
  return bytes;
}

/** The zip as one Blob: each file's header and bytes, then the central directory and its end. */
export function zipStore(entries: readonly ZipEntry[]): Blob {
  const encoder = new TextEncoder();
  const parts: BlobPart[] = [];
  const directory: Uint8Array[] = [];
  let offset = 0;
  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const local = header(entry, name, crc, null);
    parts.push(local as BlobPart, entry.data as BlobPart);
    directory.push(header(entry, name, crc, offset));
    offset += local.length + entry.data.length;
  }
  const size = directory.reduce((sum, record) => sum + record.length, 0);
  const end = new Uint8Array(22);
  const view = new DataView(end.buffer);
  view.setUint32(0, 0x06054b50, true);
  view.setUint16(8, entries.length, true);
  view.setUint16(10, entries.length, true);
  view.setUint32(12, size, true);
  view.setUint32(16, offset, true);
  return new Blob([...parts, ...(directory as BlobPart[]), end as BlobPart], {
    type: 'application/zip',
  });
}
